import React, { useEffect } from 'react';
import {
    LegendBackdrop,
    LegendCloseButton,
    LegendEntry,
    LegendPanel,
    LegendSection,
    LegendSectionTitle,
    LegendSubTitle,
    LegendText,
} from './SocketStatsBar.style';

interface PropsType {
    isOpen: boolean;
    onClose: () => void;
}

export const SocketStatsBarLegend = ({ isOpen, onClose }: PropsType): React.ReactElement | null => {
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return (): void => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <LegendBackdrop
                type='button'
                aria-label='Close legend'
                onClick={onClose}
            />
            <LegendPanel
                role='dialog'
                aria-labelledby='socket-stats-legend-title'
            >
                <LegendCloseButton
                    type='button'
                    onClick={onClose}
                >
                    ×
                </LegendCloseButton>
                <LegendSectionTitle id='socket-stats-legend-title'>Legend — WS stats (dev)</LegendSectionTitle>

                <LegendSection>
                    <LegendSubTitle>Number format</LegendSubTitle>
                    <LegendText>
                        Everywhere on chips: <strong>active / total</strong>.
                    </LegendText>
                    <LegendEntry data-term='active'>
                        How many map entries are currently <strong>observed by MobX</strong> (component, computed,
                        autorun). For maps with subscriptions this equals how many <code>SubscriptionValue</code>{' '}
                        messages with <code>active: true</code> we keep on the Events socket.
                    </LegendEntry>
                    <LegendEntry data-term='total'>
                        How many keys have ever been stored in the in-memory <strong>cache</strong>. After leaving a
                        page, <strong>active drops to 0</strong>, but <strong>total usually keeps growing</strong> (no
                        eviction). This is not the number of sockets.
                    </LegendEntry>
                </LegendSection>

                <LegendSection>
                    <LegendSubTitle>Connection</LegendSubTitle>
                    <LegendEntry data-term='Events socket'>
                        A single WebSocket for the “Events” channel. <strong>open</strong> when anything has active &gt;
                        0; <strong>closed</strong> when all MobX subscriptions are gone (e.g. casino-only).
                    </LegendEntry>
                    <LegendEntry data-term='holders'>
                        Sum of <strong>active</strong> across all maps (including selection). Should equal wire active +
                        selection active.
                    </LegendEntry>
                    <LegendEntry data-term='wire active / cache'>
                        Sum of active (and total cache) only for maps that send subscriptions on the wire — excludes
                        selection.
                    </LegendEntry>
                </LegendSection>

                <LegendSection>
                    <LegendSubTitle>Chips (maps)</LegendSubTitle>
                    <LegendEntry data-term='event'>ModelEvent — match / race data.</LegendEntry>
                    <LegendEntry data-term='market'>
                        ModelMarket — full market + selections (event page, odds).
                    </LegendEntry>
                    <LegendEntry data-term='evtMkt'>
                        ModelEventMarkets — market list (light metadata) per event.
                    </LegendEntry>
                    <LegendEntry data-term='evtMktAll'>
                        ModelEventMarketsAll — full market list (e.g. racing: <code>marketRaceWinnerAll</code>).
                    </LegendEntry>
                    <LegendEntry data-term='query'>
                        QueryEvents — one subscription per list query (sport, in-play…).
                    </LegendEntry>
                    <LegendEntry data-term='selection (cache)'>
                        No own WS subscription — entries come from market payloads. “(cache)” means memory / UI
                        observation only.
                    </LegendEntry>
                    <LegendEntry data-term='sport / comp / rab'>
                        ModelSport, ModelCompetition, ModelRabMarkets.
                    </LegendEntry>
                </LegendSection>

                <LegendSection>
                    <LegendSubTitle>Chip colours</LegendSubTitle>
                    <LegendText>
                        Yellow / red = high <strong>active</strong> (dev thresholds). On an open sportsbook, large
                        active counts are normal; a problem is high active <strong>after leaving</strong> the
                        sportsbook.
                    </LegendText>
                </LegendSection>

                <LegendSection>
                    <LegendSubTitle>Quick “leak” check</LegendSubTitle>
                    <LegendText>
                        Go to casino (or another page without event lists): expect <strong>socket closed</strong>,{' '}
                        <strong>holders 0</strong>, <strong>wire active 0</strong>. High total with zero active = RAM
                        cache, not hanging connections.
                    </LegendText>
                    <LegendText>
                        Console: <code>$appState.common.models.getSocketSubscriptionDebugSnapshot()</code>
                    </LegendText>
                </LegendSection>
            </LegendPanel>
        </>
    );
};
